import { Modal, Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// ============================================================
// 발송 타입별 라벨/사유 문구
// ============================================================
const TYPE_LABEL = {
  SMS: '단문(SMS)',
  LMS: '장문(LMS)',
  MMS: '멀티미디어 문자(MMS)',
};

// 타입이 결정된 이유 (장문/MMS만 표기)
const TYPE_REASON = {
  SMS: '',
  LMS: '입력하신 내용이 90바이트(한글 30자)를 초과하여 ',
  MMS: '사진이 첨부되어 ',
};

const formatCount = value => Number(value || 0).toLocaleString();

// ============================================================
// Styled
// ============================================================
const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '440px',
  backgroundColor: '#fff',
  boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2)',
  borderRadius: '16px',
  padding: '32px',
});

const Title = styled(Typography)({
  fontSize: '20px',
  fontWeight: 700,
  color: '#333',
  textAlign: 'center',
  marginBottom: '20px',
});

const Line = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#333',
  lineHeight: 1.6,
  textAlign: 'center',
});

const Strong = styled('span')({
  fontWeight: 700,
  color: '#4ab3bc',
});

const Warn = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#e57373',
  lineHeight: 1.5,
  textAlign: 'center',
  marginBottom: '12px',
});

const BalanceLine = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#888',
  textAlign: 'center',
  margin: '8px 0 24px',
});

const Question = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#333',
  textAlign: 'center',
  marginBottom: '24px',
});

const NoPhoneBox = styled(Box)({
  backgroundColor: '#FFF4F4',
  border: '1px solid #F5C2C2',
  borderRadius: '8px',
  padding: '12px 14px',
  marginBottom: '20px',
});

const NoPhoneTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#d32f2f',
  marginBottom: '6px',
});

const NoPhoneNames = styled(Typography)({
  fontSize: '13px',
  color: '#555',
  lineHeight: 1.6,
  maxHeight: '96px',
  overflowY: 'auto',
  wordBreak: 'keep-all',
});

const ButtonRow = styled(Box)({
  display: 'flex',
  gap: '12px',
});

const CancelButton = styled(Button)({
  flex: 1,
  height: '48px',
  borderRadius: '8px',
  border: '1px solid #E0E0E0',
  backgroundColor: '#fff',
  fontSize: '18px',
  fontWeight: 500,
  color: '#333',
  '&:hover': { backgroundColor: '#F5F5F5' },
});

const PrimaryButton = styled(Button)({
  flex: 1,
  height: '48px',
  borderRadius: '8px',
  backgroundColor: '#4ab3bc',
  fontSize: '18px',
  fontWeight: 600,
  color: '#fff',
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#3a9fa8', boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#cfe7e9', color: '#fff' },
});

// ============================================================
// 발송 확인 모달
// ============================================================
/**
 * @param {boolean} open
 * @param {function} onClose
 * @param {function} onConfirm  - 발송 확정
 * @param {function} onCharge   - 잔액 부족 시 충전 화면 열기
 * @param {'SMS'|'LMS'|'MMS'} messageType
 * @param {number} recipientCount - 수신 인원 (전화번호 있는 회원만)
 * @param {Array} noPhoneMembers - 전화번호가 없어 제외되는 회원
 * @param {number} perCost  - 1명당 차감 건수
 * @param {number} totalCost - 총 차감 건수
 * @param {number} balance  - 현재 잔액(건)
 * @param {boolean} isAd
 * @param {boolean} loading - 발송 진행 중
 */
const SmsSendConfirmModal = ({
  open,
  onClose,
  onConfirm,
  onCharge,
  messageType = 'SMS',
  recipientCount = 0,
  noPhoneMembers = [],
  perCost = 1,
  totalCost = 0,
  balance = 0,
  isAd = false,
  loading = false,
}) => {
  const { t } = useTranslation();

  const insufficient = totalCost > balance;
  const shortBy = Math.max(0, totalCost - balance);
  const afterBalance = balance - totalCost;

  const noPhoneNotice = noPhoneMembers.length > 0 && (
    <NoPhoneBox>
      <NoPhoneTitle>
        {t('전화번호가 없는 회원')} {formatCount(noPhoneMembers.length)}
        {t('명은 발송에서 제외됩니다.')}
      </NoPhoneTitle>
      <NoPhoneNames>{noPhoneMembers.map(m => m.name || '-').join(', ')}</NoPhoneNames>
    </NoPhoneBox>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        {recipientCount === 0 ? (
          <>
            <Title>{t('발송할 수 있는 수신자가 없습니다')}</Title>
            {noPhoneNotice}
            <BalanceLine>{t('회원정보에 전화번호를 등록한 뒤 다시 발송해 주세요.')}</BalanceLine>
            <ButtonRow>
              <CancelButton onClick={onClose}>{t('닫기')}</CancelButton>
            </ButtonRow>
          </>
        ) : insufficient ? (
          <>
            <Title>{t('발송 가능 건수가 부족합니다')}</Title>
            <Line>
              {t('필요')} <Strong>{formatCount(totalCost)}</Strong>
              {t('건 · 현재 잔액')} {formatCount(balance)}
              {t('건')} ({t('부족')} {formatCount(shortBy)}
              {t('건')})
            </Line>
            <BalanceLine>{t('충전 후 다시 발송해 주세요.')}</BalanceLine>
            <ButtonRow>
              <CancelButton onClick={onClose}>{t('닫기')}</CancelButton>
              <PrimaryButton onClick={onCharge}>{t('충전하기')}</PrimaryButton>
            </ButtonRow>
          </>
        ) : (
          <>
            <Title>{t('문자 발송 확인')}</Title>
            {isAd && (
              <Warn>
                {t('광고 문자는 머리말·무료수신거부 문구가 자동 추가되어 글자 수가 늘어납니다.')}
              </Warn>
            )}
            <Line>
              {t(TYPE_REASON[messageType])}
              <Strong>{t(TYPE_LABEL[messageType])}</Strong>
              {t('(으)로 발송됩니다.')}
            </Line>
            <Line>
              {t('수신')} <Strong>{formatCount(recipientCount)}</Strong>
              {t('명 · 1명당')} {perCost}
              {t('건 → 총')} <Strong>{formatCount(totalCost)}</Strong>
              {t('건이 차감됩니다.')}
            </Line>
            <BalanceLine>
              {t('현재 잔액')} {formatCount(balance)}
              {t('건 → 발송 후')} {formatCount(afterBalance)}
              {t('건')}
            </BalanceLine>
            {noPhoneNotice}
            <Question>{t('발송하시겠습니까?')}</Question>
            <ButtonRow>
              <CancelButton onClick={onClose} disabled={loading}>
                {t('취소')}
              </CancelButton>
              <PrimaryButton onClick={onConfirm} disabled={loading}>
                {loading ? t('발송중...') : t('발송')}
              </PrimaryButton>
            </ButtonRow>
          </>
        )}
      </ModalContainer>
    </Modal>
  );
};

export default SmsSendConfirmModal;
